import {
  Currencies,
  CurrencyNetwork,
  DEPOSIT_ADDRESS_TYPE,
  DepositAddressInformation,
  HubCurrencies,
} from "@letsbit/malcolmlb";
import { BackButton } from "components/BackButton";
import { useCurrencies, useCurrency } from "hooks/user/useCurrencies";
import { FormattedMessage } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { messages } from "./messages";
import LoadingAnimation from "components/LoadingAnimation";
import { DepositPersonalAccountArs } from "components/DepositComponents/DepositPersonalAccountArs";
import { DepositPersonalAccountCrypto } from "components/DepositComponents/DepositPersonalAccountCrypto";
import { DepositOmnibusAccount } from "components/DepositComponents/DepositOmnibusAccount";
import { Button, Card, CircleIconContainer } from "@letsbit/milkylb";
import { DepositUnavailable } from "components/DepositComponents/DepositUnavailable";
import { DepositPersonalAccountBridge } from "components/DepositComponents/DepositPersonalAccountBridge";
import { DepositSkeleton } from "components/DepositComponents/DepositSkeleton";
import { useMemo } from "react";
import { useGetMembers } from "hooks/partnerApi/useGetMembers";
import { useGetMemberDepositAddress } from "hooks/partnerApi/useGetMemberDepositAddress";
import { ModalSelector } from "components/ModalSelector";
import { filterCurrencies } from "utils/filters";
import { useThemeToBody } from "hooks/utils/useThemeToBody";

export const MemberDepositPage: React.FC = () => {
  const navigate = useNavigate();
  const { getThemeUrlIcon } = useThemeToBody();
  const { currency_id, payment_network_id, uid } = useParams<{
    currency_id: string;
    payment_network_id: string;
    uid: string;
  }>();
  const { data: currencies } = useCurrencies();
  const currency = useCurrency(currency_id || "");

  const { data: members } = useGetMembers();
  const { name } = useMemo(() => {
    const member = members?.flat().find((member) => member.uid === uid);
    const memberName =
      member?.natural_person_data?.first_name +
        " " +
        member?.natural_person_data?.last_name || "";
    return {
      name: memberName,
    };
  }, [members, uid]);

  const { data: depositAddressInformation } = useGetMemberDepositAddress(
    currency_id as Currencies,
    payment_network_id,
    uid,
  );

  const setPaymentNetwork = (cn: CurrencyNetwork) => {
    navigate(`./deposit/${currency_id}/${cn.payment_network.id}`);
  };

  const RenderComponent = ({
    currency,
    paymentNetworkId,
    depositAddress,
  }: {
    currency?: HubCurrencies;
    paymentNetworkId: string;
    depositAddress: DepositAddressInformation;
  }) => {
    const pn = currency?.currency_networks.find(
      (cn) => cn.payment_network.id === paymentNetworkId,
    );
    if (!currency || !pn || !pn.deposit_enabled) {
      return <DepositUnavailable />;
    }
    switch (depositAddress?.type) {
      case DEPOSIT_ADDRESS_TYPE.PERSONAL_ACCOUNT_ARS:
        return (
          <DepositPersonalAccountArs
            currencyId={currency.id}
            paymentNetworkId={paymentNetworkId}
            depositAddress={depositAddress}
            setPaymentNetwork={setPaymentNetwork}
            viewerOnly
          />
        );
      case DEPOSIT_ADDRESS_TYPE.PERSONAL_ACCOUNT_CRYPTO:
        return (
          <DepositPersonalAccountCrypto
            currencyId={currency.id}
            paymentNetworkId={paymentNetworkId}
            depositAddress={depositAddress}
            setPaymentNetwork={setPaymentNetwork}
            viewerOnly
          />
        );
      case DEPOSIT_ADDRESS_TYPE.PERSONAL_ACCOUNT_BRIDGE:
        return (
          <DepositPersonalAccountBridge
            currencyId={currency.id}
            paymentNetworkId={paymentNetworkId}
            depositAddress={depositAddress}
            setPaymentNetwork={setPaymentNetwork}
            viewerOnly
          />
        );
      case DEPOSIT_ADDRESS_TYPE.OMNIBUS_ACCOUNT:
        return (
          <DepositOmnibusAccount
            currencyId={currency.id}
            paymentNetworkId={paymentNetworkId}
            depositAddress={depositAddress}
            setPaymentNetwork={setPaymentNetwork}
            viewerOnly
          />
        );
    }
  };

  return currency_id && payment_network_id ? (
    <div className="flex h-full flex-col">
      <div className="mb-6 flex items-center justify-between border-b border-[--milkylb-color-secondary-200] pb-8">
        <div className="flex flex-col items-start">
          <BackButton className="mb-4" />
          <h1 className="font-['Tomato_Grotesk'] text-4xl font-bold">
            <FormattedMessage
              {...messages.title}
              values={{
                ticker: currency?.name || currency_id?.toUpperCase(),
                name: name,
              }}
            />
          </h1>
        </div>
        <div className="flex justify-center gap-x-4">
          <ModalSelector
            title={
              <h2 className="mb-8 text-center text-xl font-bold">
                <FormattedMessage {...messages.modalTitle} />
              </h2>
            }
            filter={filterCurrencies}
            optionComponent={({ element }) => (
              <Card
                className={`shrink-0`}
                key={element.id}
                title={element.ticker_id.toUpperCase() || ""}
                subtitle={element.name || ""}
                icon={
                  <CircleIconContainer
                    icon={getThemeUrlIcon(element.icon_url)}
                  />
                }
              ></Card>
            )}
            options={currencies?.filter((c) => c.deposit_enabled)}
            onSelectOption={(curr) => {
              const paymentNetwork =
                curr &&
                curr.deposit_enabled &&
                curr.currency_networks.length > 0
                  ? curr.currency_networks.find((cn) => cn.deposit_enabled)
                      ?.payment_network
                  : undefined;
              navigate(
                `/logged/members/${uid}/platform/deposit-address/${curr.id}/${paymentNetwork?.id}`,
              );
            }}
          >
            {({ onClick }) => (
              <Button onClick={onClick} fill="outline">
                <FormattedMessage {...messages.buttonLabel} />
              </Button>
            )}
          </ModalSelector>
        </div>
      </div>
      {depositAddressInformation ? (
        RenderComponent({
          currency: currency,
          paymentNetworkId: payment_network_id,
          depositAddress: depositAddressInformation,
        })
      ) : (
        <DepositSkeleton />
      )}
    </div>
  ) : (
    <LoadingAnimation />
  );
};
